import { RefEntityTypeEnum, RefStatusTypeEnum, EmployeeType, AppraisalStatus } from './enums';

export interface ITuple<T1, T2> {
    item1: T1;
    item2: T2;
}

export interface IEnumerable<T> extends Array<T> {
}

export interface ICollection<T> extends IEnumerable<T> {
}

export interface IOrderedEnumerable<T> extends IEnumerable<T> {
}

export interface IList<T> extends ICollection<T> {
}

export class CompanyGridViewModel
{
    companyId: number;
    code: string;
    name: string;
    departmentsCount: number;
    status: LookupViewModel;
}

export class CompanyViewModel
{
    companyId: number;
    code: string;
    name: string;
    refStatusTypeId: RefStatusTypeEnum;
}

export class DepartmentGridViewModel
{
    departmentId: number;
    code: string;
    name: string;
    companyId: number;
    companyCode: string;
    jobsCount: number;
    status: LookupViewModel;
    paretnRefStatusId: RefStatusTypeEnum;
}

export class DepartmentViewModel
{
    departmentId: number;
    code: string;
    name: string;
    refStatusTypeId: RefStatusTypeEnum;
    companyId: number;
}

export class SectionViewModel
{
    sectionId: number;
    mappingId: number;
    isCommentRequired: boolean;
    parent: RefEntityTypeEnum;
    parentId: number;
    factorCode: string;
    shortDescription: string;
    description: string;
    weight: number;
    refStatusTypeId: RefStatusTypeEnum;
    status: LookupViewModel;
}

export class AppraisalDetailRowViewModel extends SectionViewModel
{
    employeeAppraisalScoreId: number;
    comment: string;
    selectedValue: number;
}

export class DeleteAppraisalsFilterViewModel
{
    companyId?: number;
    departmentId?: number;
    positionId?: number;
}

export class EmployeeAppraisalCalculationViewModel
{
    employeeAppraisalId: number;
    behavioralElements: IList<AppraisalDetailRowViewModel>;
    performanceElements: IList<AppraisalDetailRowViewModel>;
}

export class EmployeeAppraisalDetailViewModel extends EmployeeAppraisalCalculationViewModel
{
    formId: number;
    employeeInfo: EmployeeInfoViewModel;
    additionalComments: string;
    developmentObjectives: string;
    verificationReview: VerificationReviewViewModel;
    completedDate?: Date;
    ratings: IList<EmployeeAppraisalRatingViewModel>;
}

export class EmployeeAppraisalFilterViewModel
{
    companyId?: number;
    departmentId?: number;
    positionId?: number;
    formId?: number;
    year?: number;
    status: AppraisalStatus;
}

export class EmployeeAppraisalGridViewModel
{
    employeeAppraisalId: number;
    employeeId: number;
    companyCode: string;
    departmentTitle: string;
    jobLevelCode: string;
    score: any;
    completedDate?: Date;
    ceo: boolean;
    executive: boolean;
    name: string;
    executiveOverrideRating?: number;
    ceoOverrideRating?: number;
    managerName: string;
    evaluationDate?: Date;
    position: string;
}

export class EmployeeAppraisalRatingViewModel
{
    ratingId: number;
    code: string;
    description: string;
    score: number;
}

export class EmployeeAppraisalViewModel
{
    employeeAppraisalId: number;
    employeeId: number;
    formId: number;
    evaluationDate?: Date;
    responsibilityAreas: string;
    employeeSignature: string;
    employeeSignDate?: Date;
    directSupervisorSignature: string;
    supervisorSignDate?: Date;
}

export class EmployeeInfoViewModel
{
    employeeId: number;
    name: string;
    departmentName: string;
    positionTitle: string;
    hireDate?: Date;
    parentEmployeeId?: number;
    managerName: string;
    evaluationDate?: Date;
    responsibilityAreas: string;
}

export class ReviewApprovalViewModel
{
    employeeAppraisalId: number;
    employeeName: string;
    directSupervisorName: string;
    reviewDate: Date;
    calculatedOveralRating?: number;
    overrideRating?: number;
    employeeType: EmployeeType;
}

export class VerificationReviewViewModel
{
    employeeSignature: string;
    employeeSignDate?: Date;
    directSupervisorSignature: string;
    supervisorSignDate?: Date;
}

export class EmployeeGridViewModel
{
    employeeId: number;
    firstName: string;
    lastName: string;
    companyId: number;
    companyCode: string;
    departmentId: number;
    departmentName: string;
    positionId: number;
    positionTitle: string;
    jobLevelId: number;
    jobLevelCode: string;
    status: LookupViewModel;
    name: string;
    paretnRefStatusId: RefStatusTypeEnum;
}

export class EmployeeViewModel
{
    employeeId: number;
    sectionId?: number;
    firstName: string;
    middleInitial: string;
    lastName: string;
    companyId: number;
    companyName: string;
    departmentId: number;
    departmentName: string;
    positionId: number;
    positionTitle: string;
    jobLevelId: number;
    jobLevelCode: string;
    hireDate?: Date;
    parentEmployeeId?: number;
    managerName: string;
    salary?: number;
    salaryIncrease?: number;
    bonusEligibility?: number;
    login: boolean;
    webAdmin: boolean;
    aspNetUserId?: number;
    ceo: boolean;
    executive: boolean;
    refStatusTypeId: RefStatusTypeEnum;
    email: string;
    password: string;
    paretnRefStatusId: RefStatusTypeEnum;
}

export class FileContentViewModel
{
    base64Content: string;
}

export class ProfileViewModel
{
    employeeId: number;
    firstName: string;
    middleInitial: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
    webAdmin: boolean;
    ceo: boolean;
    executive: boolean;
}

export class FormGridViewModel
{
    formId: number;
    companyId: number;
    code: string;
    companyCode: string;
    name: string;
    title: string;
    default?: boolean;
    sorting: number;
    refStatusTypeId: RefStatusTypeEnum;
    status: LookupViewModel;
    paretnRefStatusId: RefStatusTypeEnum;
}

export class FormViewModel
{
    formId: number;
    companyId: number;
    code: string;
    name: string;
    title: string;
    default?: boolean;
    sorting: number;
    refStatusTypeId: RefStatusTypeEnum;
    sections: IList<SectionViewModel>;
}

export class JobLevelGridViewModel
{
    jobLevelId: number;
    code: string;
    description: string;
    management: boolean;
    companyId: number;
    companyCode: string;
    paretnRefStatusId: RefStatusTypeEnum;
}

export class JobLevelViewModel
{
    jobLevelId: number;
    code: string;
    description: string;
    management: boolean;
    companyId: number;
}

export class LookupViewModel
{
    id: number;
    parentId?: number;
    code: string;
    name: string;
}

export class PositionFactorCodesCheckViewModel
{
    mergePositionId: number;
    factorCodes: IList<string>;
}

export class PositionGridViewModel
{
    positionId: number;
    companyId: number;
    companyName: string;
    departmentId: number;
    departmentName: string;
    title: string;
    description: string;
    jobLevelId: number;
    jobLevelDescription: string;
    refStatusTypeId: RefStatusTypeEnum;
    status: LookupViewModel;
    paretnRefStatusId: RefStatusTypeEnum;
}

export class PositionViewModel
{
    positionId: number;
    mergePositionId: number;
    companyId: number;
    companyCode: string;
    departmentId: number;
    departmentCode: string;
    title: string;
    description: string;
    jobLevelId: number;
    jobLevelCode: string;
    refStatusTypeId: RefStatusTypeEnum;
    sectionBtitle: string;
    weightedPercent: number;
    sections: IList<SectionViewModel>;
}

export class RatingGridViewModel
{
    ratingId: number;
    companyId: number;
    companyCode: string;
    formId: number;
    formCode: string;
    code: string;
    description: string;
    score: number;
    paretnRefStatusId: RefStatusTypeEnum;
}

export class RatingViewModel
{
    ratingId: number;
    companyId: number;
    formId: number;
    code: string;
    description: string;
    score: number;
}

export class LookupRequestViewModel
{
    entityType: RefEntityTypeEnum;
    sourceYear: number;
    parentIds: IList<number>;
    isCopied: boolean;
}

export class ResponseBase
{
    success: boolean;
    errorMessages: IList<string>;
    userFriendlyErrorMessage: string;
    userFriendlyWarningMessage: string;
}

export class Response<T> extends ResponseBase
{
    result: T;
}

export class SectionGridViewModel
{
    sectionId: number;
    mappingId: number;
    parent: RefEntityTypeEnum;
    parentId: number;
    positionId: number;
    factorCode: string;
    shortDescription: string;
    description: string;
    weight: number;
    refStatusTypeId: RefStatusTypeEnum;
    status: LookupViewModel;
}

export class LoginResult
{
    tokenResult: TokenResult;
    profile: ProfileViewModel;
}

export class TokenResult
{
    token: string;
    expiration: Date;
}

export class UserCompanyRangesViewModel
{
    userIds: IList<number>;
    companyIds: IList<number>;
}

export class UserCompanyViewModel
{
    company: CompanyViewModel;
}

export class UserGridViewModel
{
    id: number;
    userName: string;
    firstName: string;
    middleInitial: string;
    lastName: string;
    userCompany: IList<UserCompanyViewModel>;
}

export class UserViewModel
{
    id: number;
    userName: string;
    firstName: string;
    middleInitial: string;
    lastName: string;
    companyIds: IList<number>;
}

export class CodeValidationViewModel
{
    code: string;
    entityType: RefEntityTypeEnum;
    entityId: number;
    parentEntityType: RefEntityTypeEnum;
    parentEntityId: number;
}

export class SecureLinkViewModel
{
    isValid: boolean;
    email: string;
}

export class CloneEntitiesViewModel
{
    entityType: RefEntityTypeEnum;
    entityIds: IList<number>;
}